import { useState, useEffect, createContext, useContext, ReactNode } from 'react'
import type { User } from '@/types'

interface AuthContextType {
  user: User | null
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>
  signup: (data: SignupData) => Promise<{ success: boolean; error?: string }>
  logout: () => Promise<void>
  isLoading: boolean
  isAuthenticated: boolean
}

interface SignupData {
  email: string
  password: string
  fullName: string
  canadianAddress: {
    street: string
    city: string
    province: string
    postalCode: string
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const API_BASE = import.meta.env.DEV ? 'http://localhost:8787' : 'https://shopglobal-backend.finhub.workers.dev'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Check for existing session on mount
  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      verifySession(token)
    } else {
      setIsLoading(false)
    }
  }, [])

  const verifySession = async (token: string) => {
    try {
      const response = await fetch(`${API_BASE}/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const { success, user: userData } = await response.json()
        if (success) {
          setUser({
            ...userData,
            canadianAddress: JSON.parse(userData.canadian_address)
          })
        }
      }
    } catch (error) {
      console.error('Session verification failed:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(`${API_BASE}/auth/signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        const { user, session } = data
        const parsedUser: User = {
          ...user,
          canadianAddress: JSON.parse(user.canadian_address)
        }

        setUser(parsedUser)
        localStorage.setItem('auth_token', session.token)

        return { success: true }
      } else {
        return { success: false, error: data.error || 'Login failed' }
      }
    } catch (error) {
      console.error('Login error:', error)
      return { success: false, error: 'Network error. Please try again.' }
    }
  }

  const signup = async (signupData: SignupData) => {
    try {
      const response = await fetch(`${API_BASE}/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(signupData),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        const { user } = data
        const parsedUser: User = {
          ...user,
          canadianAddress: JSON.parse(user.canadian_address)
        }

        setUser(parsedUser)
        // Note: For MVP, we'll auto-login after signup
        // In production, you might want to verify email first

        return { success: true }
      } else {
        return { success: false, error: data.error || 'Signup failed' }
      }
    } catch (error) {
      console.error('Signup error:', error)
      return { success: false, error: 'Network error. Please try again.' }
    }
  }

  const logout = async () => {
    // Clear local state and storage
    setUser(null)
    localStorage.removeItem('auth_token')

    // In production, you might want to call an API to invalidate the session
  }

  const value: AuthContextType = {
    user,
    login,
    signup,
    logout,
    isLoading,
    isAuthenticated: !!user,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
